"use client";

/**
 * The composer's history rows: the account transcript merged over the local
 * mirror, and the words the rows are drawn with. Shared by the home pill's
 * history (bash-home/PillHistory), which the dock hosts on every screen.
 * `useComposerHistoryPages` is the paged transcript the chat reads.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ComposerHistoryCursor } from "@bash/shared";
import {
  fetchComposerHistory,
  type ComposerHistoryPage,
} from "@/lib/composer/converse-client";
import { fetchAgentHistory } from "@/lib/api/team";
import {
  mergeHistory,
  useComposerHistoryStore,
  type ComposerHistoryEntry,
} from "@/stores/composerHistoryStore";

/** Plain words for the tools a query ran. */
const TOOL_WORDS: Record<string, string> = {
  web_search: "web search",
  web_fetch: "read a page",
  fetch_recent_tasks: "checked your tasks",
  recall_from_nami: "checked memory",
  save_to_nami: "saved to Nami",
  os_snapshot: "read your screen",
  os_look: "looked at your screen",
  os_apps: "checked your apps",
  run_verb: "quick Mac action",
};

/** A card id in plain words (2a): app.gmail.GMAIL_SEND_EMAIL reads "gmail: send email";
 *  vestra.routines.pause reads "routines: pause"; anything else is null. */
export function cardWords(id: string): string | null {
  const m = /^(vestra|app|mac|channel)\.([^.\s]+)(?:\.(\S+))?$/.exec(id);
  if (!m) return null;
  const [, family, second, third] = m;
  // The leading segment of a Composio slug repeats the toolkit (GMAIL_SEND_EMAIL), so it is
  // dropped. A slug with no underscore has nothing after it, and dropping it left "foo: " with no
  // name at all, so in that case the whole slug IS the name.
  if (family === "app" && third)
    return `${second}: ${(third.split("_").slice(1).join(" ") || third).toLowerCase()}`;
  return third ? `${second}: ${third.replace(/_/g, " ")}` : second!;
}

export function toolWords(tools: string[] | undefined): string | null {
  if (!tools?.length) return null;
  // The door and its target say one thing: when a card id follows run_tool, the id's words
  // stand for both. R-2a-25: TOOL_WORDS gains no run_tool row, so every 1a and 1b row already
  // in the account (run_tool with nothing after it) renders exactly as it does today.
  const shown = tools.filter(
    (t, i) => !(t === "run_tool" && cardWords(tools[i + 1] ?? "") !== null)
  );
  return shown
    .map((t) => TOOL_WORDS[t] ?? cardWords(t) ?? t.replace(/_/g, " "))
    .join(", ");
}

/** Quiet relative stamp — the card is a glance, not an audit log. The team
 *  tile's (train 1b); the chat's rows use history/HistoryTime. */
export function agoLabel(ts: number, now: number): string {
  const mins = Math.floor((now - ts) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(ts).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export interface ComposerHistoryPages {
  /** CHAT order: oldest first, the latest LAST (nearest the composer bar). */
  rows: ComposerHistoryEntry[];
  /** The server has an older page to give (the last page came back full). */
  hasMore: boolean;
  /** An older page is on its way. */
  loadingOlder: boolean;
  /** Ask for the next older page. A no-op while one is loading or when there is none. */
  loadOlder: () => void;
  /** The newest page landed (or failed) at least once for the CURRENT agent.
   *  Before this, every row in `rows` is mirror-only — the mirror never
   *  writes `receipt`/`task`/`tools` (this file's own header comment), so a
   *  run or task row painted now is missing whatever of those it will turn
   *  out to have. A reader gates a receipt/task card on this to avoid
   *  showing the short guess and then growing it once the real page lands. */
  settled: boolean;
}

/** What the server gave for ONE handle: the newest page, every older page
 *  that landed after it (newest first, in the order they came), and whether
 *  it has more to give. */
interface ServerPages {
  /** The handle these pages were read for; `undefined` is the lead. */
  agent?: string;
  newest: ComposerHistoryPage;
  older: ComposerHistoryEntry[];
  hasMore: boolean;
  loadingOlder: boolean;
}

/**
 * The whole transcript, a page at a time (spec 2026-09-24 §3). The newest page
 * is fetched on mount and merged over the local mirror, which renders instantly
 * while it is in flight and stays the display if it fails; older pages are
 * the server's alone, appended by `loadOlder()` and never merged with the
 * mirror. A failed older page stops the spinner and keeps `hasMore`, so the
 * next scroll to the top asks again. An old server sends no cursor, so
 * `hasMore` stays false.
 *
 * Agents first (train 1b): with a handle it is THAT agent's transcript
 * (GET /api/team/:handle/history, one page, no cursor, so it never pages) over
 * the mirror rows filed under it; with none it is the composer's own
 * (GET /api/composer/history) over the rows filed under nobody, which is every
 * row written before agents existed.
 */
export function useComposerHistoryPages(agent?: string): ComposerHistoryPages {
  const local = useComposerHistoryStore((s) => s.entries);
  // The server's answer is held WITH the handle it was read for, and only
  // counts while that is still the handle on screen. A switched pick then
  // falls back to the mirror in the same render the switch happens in,
  // instead of flashing the previous agent's transcript until the new read
  // lands, and it does so without a reset write inside the effect. The new
  // handle's first page replaces the whole of it: older pages, cursor and
  // hasMore start again.
  const [server, setServer] = useState<ServerPages | null>(null);
  // Refs, not state: two scroll events in one frame must see the same answer.
  const cursor = useRef<ComposerHistoryCursor | undefined>(undefined);
  const busy = useRef(false);
  const mounted = useRef(false);
  /** Bumped at every handle: an older page asked for under an earlier one
   *  lands on nothing. */
  const read = useRef(0);

  useEffect(() => {
    mounted.current = true;
    read.current += 1;
    cursor.current = undefined;
    busy.current = false;
    let alive = true;
    void (
      agent
        ? fetchAgentHistory(agent).then((entries): ComposerHistoryPage => ({
            entries,
          }))
        : fetchComposerHistory()
    ).then((page) => {
      if (!alive) return;
      cursor.current = page.nextCursor;
      setServer({
        agent,
        newest: page,
        older: [],
        hasMore: Boolean(page.nextCursor),
        loadingOlder: false,
      });
    });
    return () => {
      alive = false;
      mounted.current = false;
    };
  }, [agent]);

  const loadOlder = useCallback((): void => {
    const from = cursor.current;
    if (busy.current || !from) return;
    const at = read.current;
    busy.current = true;
    setServer((s) => (s ? { ...s, loadingOlder: true } : s));
    void fetchComposerHistory(from).then((page) => {
      if (!mounted.current || read.current !== at) return;
      busy.current = false;
      if (!page.failed) cursor.current = page.nextCursor;
      setServer((s) =>
        !s
          ? s
          : page.failed
            ? { ...s, loadingOlder: false }
            : {
                ...s,
                loadingOlder: false,
                hasMore: Boolean(page.nextCursor),
                older: [...s.older, ...page.entries],
              }
      );
    });
  }, []);

  const fresh = server && server.agent === agent ? server : null;
  const rows = useMemo(() => {
    const mine = local.filter((e) => (agent ? e.agent === agent : !e.agent));
    const first =
      fresh === null
        ? mine
        : mergeHistory(
            fresh.newest.entries,
            mine,
            Boolean(fresh.newest.nextCursor)
          );
    const seen = new Set(first.map((e) => e.id));
    const all = [...first];
    for (const e of fresh?.older ?? []) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      all.push(e);
    }
    return all.reverse();
  }, [fresh, local, agent]);

  return {
    rows,
    hasMore: fresh?.hasMore ?? false,
    loadingOlder: fresh?.loadingOlder ?? false,
    loadOlder,
    settled: fresh !== null,
  };
}
